import React from "react";

/**
 * Decorative market panel shown beside the auth forms on desktop.
 *
 * The motif is drawn as inline SVG rather than shipped as an image: it scales,
 * costs no request, and follows the theme through currentColor. It is
 * deliberately abstract — it is not real price data and must never be read as
 * a market claim, so it carries no axes, no numbers and no timeframe.
 *
 * The feature list is the platform's actual surface (these routes exist in
 * App.js), not marketing copy.
 *
 * Hidden from assistive tech and collapsed entirely below 900px.
 */
export default function AuthAside({ exchangeName }) {
  return (
    <aside className="auth-aside">
      <div className="auth-aside__grid" aria-hidden="true" />

      <div className="auth-aside__body">
        <h2 className="auth-aside__headline">
          Trade spot, futures
          <br />
          and options.
        </h2>
        <p className="auth-aside__lede">
          One account for markets, wallets and settlement
          {exchangeName ? ` on ${exchangeName}` : ""}.
        </p>

        <svg
          className="auth-aside__chart"
          viewBox="0 0 320 120"
          fill="none"
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <linearGradient id="authTrend" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.15" />
              <stop offset="55%" stopColor="currentColor" stopOpacity="0.7" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0.95" />
            </linearGradient>
          </defs>

          {/* Candles — wick line plus body, ascending drift with pullbacks */}
          {[
            [8, 74, 96, 80, 90],
            [30, 66, 92, 72, 86],
            [52, 70, 88, 74, 84],
            [74, 54, 82, 60, 78],
            [96, 58, 80, 62, 74],
            [118, 44, 74, 50, 70],
            [140, 48, 72, 52, 66],
            [162, 34, 64, 40, 60],
            [184, 40, 62, 44, 56],
            [206, 26, 56, 32, 52],
            [228, 30, 54, 34, 46],
            [250, 18, 48, 24, 44],
            [272, 24, 46, 28, 38],
            [294, 10, 40, 16, 34],
          ].map(([x, hi, lo, top, bottom], i) => (
            <g key={x} opacity={0.25 + (i / 13) * 0.6}>
              <line
                x1={x + 5}
                y1={hi}
                x2={x + 5}
                y2={lo}
                stroke="currentColor"
                strokeWidth="1"
              />
              <rect
                x={x}
                y={top}
                width="10"
                height={Math.max(bottom - top, 3)}
                rx="1.5"
                fill="currentColor"
              />
            </g>
          ))}

          {/* Trend line riding the closes */}
          <path
            d="M13 85 L35 79 L57 79 L79 69 L101 68 L123 60 L145 59 L167 50 L189 50 L211 42 L233 40 L255 34 L277 33 L299 25"
            stroke="url(#authTrend)"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        <ul className="auth-aside__features">
          {["Spot", "Futures", "Options", "Convert", "P2P", "Copy trade"].map(
            (item) => (
              <li key={item} className="auth-aside__feature">
                {item}
              </li>
            )
          )}
        </ul>
      </div>
    </aside>
  );
}
